import { injectAesthetic } from '../../../../components/library/ui/shared-styles.ts';
import '../../../../components/library/ui/app-icon.ts';
import '../../../../components/library/ui/app-button.ts';

export class GatewayHistoryTab extends HTMLElement {
  private _configs: any[] = [];
  private seed = Math.floor(Math.random() * 10000);

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  set configs(val: any[]) { 
    this._configs = Array.isArray(val) ? val : []; 
    this.render(); 
  }

  connectedCallback() {
    this.render();
  }

  private render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      ${injectAesthetic(this.seed)}
      <style>
        :host { 
          display: block; 
          color: white;
          animation: fade-in 0.3s ease-out;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .table-container { 
          background: rgba(255, 255, 255, 0.01); 
          border-radius: 12px; 
          overflow-x: auto; 
        }

        table { 
          width: 100%; 
          border-collapse: collapse; 
          text-align: left; 
          table-layout: fixed; 
          min-width: 800px; 
        }

        th { 
          text-align: left; 
          padding: 1rem; 
          color: #94a3b8; 
          font-weight: 600; 
          font-size: 0.75rem; 
          text-transform: uppercase;
          letter-spacing: 0.1em;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1); 
        }

        td { 
          padding: 1rem; 
          border-top: 1px solid rgba(255, 255, 255, 0.05); 
          font-size: 0.9rem; 
          vertical-align: middle;
          transition: background-color 0.2s;
        }

        tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }

        .version-badge {
          font-weight: 600; 
          color: white;
          font-family: var(--font-mono, monospace);
        }

        .comment-text {
          font-size: 0.75rem; 
          color: var(--text-dim); 
          margin-top: 4px;
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #cbd5e1;
        }

        .date-text {
          color: #94a3b8; 
          font-family: var(--font-mono, monospace); 
          font-size: 0.85rem;
        }

        .action-group { 
          display: flex; 
          gap: 0.5rem; 
          justify-content: flex-end;
        }

        .empty-state {
          text-align: center; 
          padding: 80px 24px; 
          color: var(--text-dim);
        }
      </style>

      <div style="display: flex; flex-direction: column; gap: 24px;">
        <div style="display: flex; justify-content: flex-end; align-items: center; padding: 0 4px;">
          <div style="display: flex; gap: 8px; align-items: center;">
            <app-button variant="secondary" size="sm" id="view-current-btn" icon="eye" data-tooltip="View/Edit Current Config"></app-button>
            <app-button variant="secondary" size="sm" id="export-config-btn" icon="download" data-tooltip="Export Current Config"></app-button>
            <app-button variant="white" size="sm" id="import-config-btn" icon="upload" data-tooltip="Import Config"></app-button>
            <input type="file" id="import-input" style="display: none;" accept=".yaml,.yml">
          </div>
        </div>
        
        <div class="sketchy-card" style="padding: 0; overflow: hidden;">
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th style="width: 220px;">Version</th>
                  <th>Applied By</th>
                  <th style="width: 240px;">Date</th>
                  <th style="width: 120px; text-align: right;">Actions</th>
                </tr>
              </thead>
              <tbody>
                ${this._configs.map(config => `
                  <tr>
                    <td>
                      <div class="version-badge">v${config.version || (config.id && config.id.substring(0, 8)) || 'unknown'}</div>
                      <span class="comment-text" data-tooltip="${config.comment || 'No comment'}">${config.comment || 'No comment'}</span>
                    </td>
                    <td>
                      <div class="user-info">
                        <app-icon name="user" size="xs" style="opacity: 0.5;"></app-icon>
                        ${config.applied_by_email || 'System'}
                      </div>
                    </td>
                    <td>
                      <div class="date-text">${new Date(config.created_at || config.timestamp).toLocaleString()}</div>
                    </td>
                    <td>
                      <div class="action-group">
                        <app-button variant="ghost" size="xs" class="view-config-btn" data-id="${config.id}" icon="code" data-tooltip="View Config"></app-button>
                        <app-button variant="ghost" size="xs" class="rollback-config-btn" data-id="${config.id}" icon="rotate-ccw" data-tooltip="Rollback" style="color: var(--accent);"></app-button>
                      </div>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            ${this._configs.length === 0 ? `
              <div class="empty-state">
                <app-icon name="history" size="lg" style="opacity: 0.2; margin-bottom: 16px;"></app-icon>
                <div style="font-family: var(--font-sketch); font-size: 1.2rem;">No configuration history available.</div>
                <div style="font-size: 0.9rem; margin-top: 8px;">History of your gateway deployments will appear here.</div>
              </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;

    this.setupListeners();
  }

  private setupListeners() {
    if (!this.shadowRoot) return;

    this.shadowRoot.querySelectorAll('.view-config-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        this.dispatchEvent(new CustomEvent('view-config', { detail: id, bubbles: true, composed: true }));
      });
    });

    this.shadowRoot.querySelectorAll('.rollback-config-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        this.dispatchEvent(new CustomEvent('rollback-config', { detail: id, bubbles: true, composed: true }));
      });
    });

    const exportBtn = this.shadowRoot.querySelector('#export-config-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('export-config', { bubbles: true, composed: true }));
      });
    }

    const importBtn = this.shadowRoot.querySelector('#import-config-btn');
    const importInput = this.shadowRoot.querySelector('#import-input') as HTMLInputElement;
    if (importBtn && importInput) {
      importBtn.addEventListener('click', () => {
        importInput.click();
      });

      importInput.addEventListener('change', (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          this.dispatchEvent(new CustomEvent('import-config', { detail: { content }, bubbles: true, composed: true }));
          // Reset input so the same file can be uploaded again if needed
          importInput.value = '';
        };
        reader.readAsText(file);
      });
    }

    const viewCurrentBtn = this.shadowRoot.querySelector('#view-current-btn');
    if (viewCurrentBtn) {
      viewCurrentBtn.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('view-current', { bubbles: true, composed: true }));
      });
    }
  }
}

if (!customElements.get('gateway-history-tab')) {
  customElements.define('gateway-history-tab', GatewayHistoryTab);
}
